import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo } from 'react';
import { useMst } from '../../../models/rootModel';
import { useAnalytics } from '../../../utils/analytics';
import { AbsoluteHeader } from '../../common/AbsoluteHeader/AbsoluteHeader';
import { BaseStepPropsV2 } from '../../common/v2/BaseStepV2.types';
import { StepV2ConfirmButton } from '../../common/v2/StepV2ConfirmButton';
import { MainButtonVariant } from '../../styledComponent/Buttons.style';
import {
  InfoV2Ver2Screen,
  InfoV2Ver2ScreenButtonWrapper,
  InfoV2Ver2ScreenContentWrapper,
  InfoV2Ver2ScreenDescription,
  InfoV2Ver2ScreenDesktop,
  InfoV2Ver2ScreenDesktopLeft,
  InfoV2Ver2ScreenDesktopRight,
  InfoV2Ver2ScreenImgDesktop,
  InfoV2Ver2ScreenImgDesktopWrapper,
  InfoV2Ver2ScreenImgMobile,
  InfoV2Ver2ScreenMobile,
  InfoV2Ver2ScreenMobileButtonWrapper,
  InfoV2Ver2ScreenMobileGradientOverlay,
  InfoV2Ver2ScreenMobileImgWrapper,
  InfoV2Ver2ScreenMobileTextWrapper,
  InfoV2Ver2ScreenSection,
  InfoV2Ver2ScreenTitle,
} from './InfoV2Ver2Step.style';
import { BaseStepV3 } from '../../common/v3/BaseStepV3';
import { InfoV2Ver2GuidesCarousel } from './InfoV2Ver2GuidesCarousel';
import { useIsMobileWebview } from '../../../hooks/useIsMobileWebview.hook';
import { makeCloudinaryUpdatedImageUrl } from '../../../utils/cloudinary';

interface SegmentProps {
  title: string;
  titleMobile?: string;
  description: string;
  backgroundImageUrl: string;
  mobileBackgroundImageUrl?: string;
}

interface InfoV2Ver2StepProps extends BaseStepPropsV2 {
  propsBySegment: Record<string, SegmentProps>;
  section: string;
  imageFullSize?: boolean;
  withGuidesCarousel?: boolean;
}

export const InfoV2Ver2Step: React.FC<InfoV2Ver2StepProps> = observer((props) => {
  const {
    nextStep,
    ctaButtonTitle,
    isActive,
    section,
    propsBySegment,
    imageFullSize,
    stepName,
    withGuidesCarousel,
    goToNamedStep,
    nextStepId,
  } = props;

  const { UserDiscoveryResponse } = useMst();
  const segment = UserDiscoveryResponse?.segment as string;
  const title = propsBySegment[segment]?.title;
  const titleMobile = propsBySegment[segment]?.titleMobile;
  const description = propsBySegment[segment]?.description;
  const backgroundImageUrl = propsBySegment[segment]?.backgroundImageUrl;
  const mobileBackgroundImageUrl = propsBySegment[segment]?.mobileBackgroundImageUrl
    ? propsBySegment[segment]?.mobileBackgroundImageUrl
    : propsBySegment[segment]?.backgroundImageUrl;

  const analytics = useAnalytics();

  const isMobileWebview = useIsMobileWebview();

  useEffect(() => {
    if (isActive) {
      analytics.track(`view infoV2`, { stepName, segment: UserDiscoveryResponse.segment, isMobileWebview });
      return window.scrollTo(0, 0);
    }
  }, [analytics, isActive, stepName, UserDiscoveryResponse.segment, isMobileWebview]);

  const handleOnClick = () => {
    analytics.track(`click infoV2`, { stepName, segment: UserDiscoveryResponse.segment, isMobileWebview });
    if (!nextStepId) return nextStep();
    goToNamedStep(nextStepId);
  };

  const onBack = () => {
    props.previousStep();
  };

  const updatedBackgroundImageUrl = useMemo(
    () => makeCloudinaryUpdatedImageUrl(backgroundImageUrl),
    [backgroundImageUrl]
  );

  return (
    <BaseStepV3 {...props}>
      <InfoV2Ver2Screen>
        <AbsoluteHeader onBack={onBack} color="black" logoColor="black" withLogoOnMobileDisplay={true} />
        <InfoV2Ver2ScreenDesktop>
          <InfoV2Ver2ScreenDesktopLeft>
            <InfoV2Ver2ScreenContentWrapper>
              <InfoV2Ver2ScreenSection>{section}</InfoV2Ver2ScreenSection>
              <InfoV2Ver2ScreenTitle dangerouslySetInnerHTML={{ __html: title }} />
              <InfoV2Ver2ScreenDescription>{description}</InfoV2Ver2ScreenDescription>
              <InfoV2Ver2ScreenButtonWrapper>
                <StepV2ConfirmButton
                  {...props}
                  isButtonDisabled={false}
                  isLoading={false}
                  onClick={handleOnClick}
                  ctaButtonTitle={ctaButtonTitle}
                  variant={MainButtonVariant.Black}
                ></StepV2ConfirmButton>
              </InfoV2Ver2ScreenButtonWrapper>
            </InfoV2Ver2ScreenContentWrapper>
          </InfoV2Ver2ScreenDesktopLeft>
          <InfoV2Ver2ScreenDesktopRight imageFullSize={imageFullSize}>
            {withGuidesCarousel ? (
              <InfoV2Ver2GuidesCarousel />
            ) : (
              <InfoV2Ver2ScreenImgDesktopWrapper>
                <InfoV2Ver2ScreenImgDesktop
                  img={updatedBackgroundImageUrl}
                  imageFullSize={imageFullSize}
                  isCentered={props.stepName === 'walkthrough_access'}
                />
              </InfoV2Ver2ScreenImgDesktopWrapper>
            )}
          </InfoV2Ver2ScreenDesktopRight>
        </InfoV2Ver2ScreenDesktop>

        <InfoV2Ver2ScreenMobile>
          <InfoV2Ver2ScreenContentWrapper withCarousel={withGuidesCarousel}>
            <InfoV2Ver2ScreenMobileTextWrapper>
              <InfoV2Ver2ScreenTitle dangerouslySetInnerHTML={{ __html: titleMobile ?? title }} />
              {description && <InfoV2Ver2ScreenDescription>{description}</InfoV2Ver2ScreenDescription>}
            </InfoV2Ver2ScreenMobileTextWrapper>
            <InfoV2Ver2ScreenMobileImgWrapper withCarousel={withGuidesCarousel}>
              {withGuidesCarousel ? (
                <InfoV2Ver2GuidesCarousel />
              ) : (
                <>
                  <InfoV2Ver2ScreenImgMobile src={mobileBackgroundImageUrl} />
                  <InfoV2Ver2ScreenMobileGradientOverlay />
                </>
              )}
            </InfoV2Ver2ScreenMobileImgWrapper>
          </InfoV2Ver2ScreenContentWrapper>
          <InfoV2Ver2ScreenMobileButtonWrapper>
            <StepV2ConfirmButton
              {...props}
              isButtonDisabled={false}
              isLoading={false}
              onClick={handleOnClick}
              ctaButtonTitle={ctaButtonTitle}
              variant={MainButtonVariant.Black}
            ></StepV2ConfirmButton>
          </InfoV2Ver2ScreenMobileButtonWrapper>
        </InfoV2Ver2ScreenMobile>
      </InfoV2Ver2Screen>
    </BaseStepV3>
  );
});
